/**
 * HomePage - Головна сторінка з підбором автозапчастин
 * 
 * Функціональність:
 * - Каскадний вибір марки та моделі авто
 * - Вибір категорії запчастин
 * - Формування пошукового запиту з фільтрами
 * 
 * @module pages/HomePage
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import '../css/HomePage.css';
import Layout from "../components/Layout";
import CustomSelect from "../components/CustomSelect";
import SEO from "../components/SEO";
import { BACKEND_URL, COMPANY_PHONES, COMPANY_EMAIL } from '../config';
import { CATEGORY_EXTRA_FILTERS, CATEGORIES_WITHOUT_BRAND_MODEL, CATEGORIES_BRAND_ONLY } from '../config/categoryFilters';
import headerImage from '../assets/images/header-2.jpg';

// ============================================================================
// CONSTANTS
// ============================================================================

const ERROR_MESSAGES = {
    BRANDS: 'Не вдалося завантажити список марок',
    MODELS: 'Не вдалося завантажити список моделей',
    CATEGORIES: 'Не вдалося завантажити список категорій',
};

const PLACEHOLDERS = {
    BRAND_LOADING: 'Завантаження...',
    BRAND_DEFAULT: 'Оберіть марку (необов\'язково)',
    MODEL_NO_BRAND: 'Спочатку оберіть марку',
    MODEL_LOADING: 'Завантаження...',
    MODEL_DEFAULT: 'Оберіть модель (необов\'язково)',
    CATEGORY_LOADING: 'Завантаження...',
    CATEGORY_DEFAULT: 'Оберіть категорію (необов\'язково)',
};

// ============================================================================
// CUSTOM HOOKS
// ============================================================================

/**
 * Хук для завантаження даних з API
 * @param {string} url - URL для завантаження даних
 * @param {Array} dependencies - Залежності для повторного завантаження
 * @returns {Object} { data, loading, error }
 */
const useFetchData = (url, dependencies = []) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!url) {
            setData([]);
            setLoading(false);
            setError(null);
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            
            try {
                const response = await fetch(url);
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const result = await response.json();
                setData(result);
            } catch (err) {
                console.error('Помилка завантаження даних:', err);
                setError(err.message);
                setData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, dependencies);

    return { data, loading, error };
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const HomePage = () => {
    // ============================================================================
    // HOOKS AND ROUTING
    // ============================================================================
    
    const navigate = useNavigate();

    // ============================================================================
    // STATE MANAGEMENT
    // ============================================================================
    
    // Вибрані значення в селектах
    const [selectedBrand, setSelectedBrand] = useState('');
    const [selectedModel, setSelectedModel] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');

    // Extra-фільтри (залежать від категорії, напр. manufacturer)
    const [extraValues,  setExtraValues]  = useState({});
    const [extraOptions, setExtraOptions] = useState({});

    // ============================================================================
    // DATA FETCHING
    // ============================================================================
    
    // Завантаження брендів (один раз при монтуванні)
    const {
        data: brands,
        loading: loadingBrands,
        error: errorBrands
    } = useFetchData(`${BACKEND_URL}/categories/brands`, []);

    // Завантаження моделей (при зміні бренду)
    const modelsUrl = useMemo(
        () => selectedBrand ? `${BACKEND_URL}/categories/brands/${selectedBrand}/models` : null,
        [selectedBrand]
    );
    
    const {
        data: models,
        loading: loadingModels,
        error: errorModels
    } = useFetchData(modelsUrl, [modelsUrl]);

    // Завантаження категорій (один раз при монтуванні)
    const {
        data: categories,
        loading: loadingCategories,
        error: errorCategories
    } = useFetchData(`${BACKEND_URL}/categories`, []);

    // ============================================================================
    // EFFECTS
    // ============================================================================
    
    // Скидання вибраної моделі при зміні бренду
    useEffect(() => {
        setSelectedModel('');
    }, [selectedBrand]);

    // Підгрузка extra-опцій при зміні категорії
    useEffect(() => {
        setExtraValues({});
        setExtraOptions({});

        if (!selectedCategory || !categories.length) return;

        const catObj = categories.find(c => c.id === Number(selectedCategory));
        if (!catObj) return;

        const extraFields = CATEGORY_EXTRA_FILTERS[catObj.slug] || [];
        if (!extraFields.length) return;

        const controller = new AbortController();
        extraFields.forEach(field => {
            if (field.source === 'manufacturers') {
                fetch(`${BACKEND_URL}/products/manufacturers?categoryId=${catObj.id}`, { signal: controller.signal })
                    .then(r => r.ok ? r.json() : [])
                    .then(data => setExtraOptions(prev => ({ ...prev, [field.key]: data || [] })))
                    .catch(err => { if (err.name !== 'AbortError') console.error(err); });
            }
        });
        return () => controller.abort();
    }, [selectedCategory, categories]);

    // ============================================================================
    // EVENT HANDLERS
    // ============================================================================
    
    /**
     * Обробник зміни вибраного бренду
     */
    const handleBrandChange = useCallback((e) => {
        setSelectedBrand(e.target.value);
    }, []);

    /**
     * Обробник зміни вибраної моделі
     */
    const handleModelChange = useCallback((e) => {
        setSelectedModel(e.target.value);
    }, []);

    /**
     * Обробник зміни вибраної категорії
     */
    const handleCategoryChange = useCallback((e) => {
        const value = e.target.value;
        setSelectedCategory(value);
        setExtraValues({});
        const catObj = categories.find(c => c.id === Number(value));
        if (catObj && CATEGORIES_WITHOUT_BRAND_MODEL.has(catObj.slug)) {
            setSelectedBrand('');
            setSelectedModel('');
        }
    }, [categories]);

    /**
     * Обробник пошуку - формує slug URL та переходить на сторінку результатів
     */
    const handleSearch = useCallback(() => {
        const brandObj    = brands.find(b => b.id === Number(selectedBrand));
        const modelObj    = models.find(m => m.id === Number(selectedModel));
        const categoryObj = categories.find(c => c.id === Number(selectedCategory));

        const brandSlug    = brandObj?.slug || brandObj?.name?.toLowerCase().replace(/\s+/g, '-');
        const modelSlug    = modelObj?.slug  || modelObj?.name?.toLowerCase().replace(/\s+/g, '-');
        const categorySlug = categoryObj?.slug;

        const extraParams = new URLSearchParams();
        Object.entries(extraValues).forEach(([key, val]) => {
            if (val) extraParams.set(key, val);
        });

        const selectedCatObj = categories.find(c => c.id === Number(selectedCategory));
        const isBrandOnly = selectedCatObj && CATEGORIES_BRAND_ONLY.has(selectedCatObj.slug);

        let path;
        if (categorySlug) {
            path = `/zapchastyny/${categorySlug}`;
            if (brandSlug) {
                path += `/${brandSlug}`;
                if (modelSlug && !isBrandOnly) path += `/${modelSlug}`;
            }
        } else {
            const params = new URLSearchParams();
            if (brandSlug) params.set('brand', brandSlug);
            if (modelSlug) params.set('model', modelSlug);
            const qs = params.toString();
            path = qs ? `/zapchastyny?${qs}` : null;
        }

        if (path) {
            const qs = extraParams.toString();
            navigate(qs ? `${path}?${qs}` : path);
        }
    }, [selectedModel, selectedBrand, selectedCategory, extraValues, navigate, brands, models, categories]);

    // ============================================================================
    // MEMOIZED VALUES
    // ============================================================================
    
    /**
     * Перевірка чи можна виконати пошук (обрано хоча б один параметр)
     */
    const isSearchEnabled = useMemo(
        () => Boolean(selectedModel || selectedCategory || selectedBrand),
        [selectedModel, selectedCategory, selectedBrand]
    );

    /**
     * Об'єднана помилка для відображення
     */
    const displayError = useMemo(() => {
        if (errorBrands) return ERROR_MESSAGES.BRANDS;
        if (errorModels) return ERROR_MESSAGES.MODELS;
        if (errorCategories) return ERROR_MESSAGES.CATEGORIES;
        return null;
    }, [errorBrands, errorModels, errorCategories]);

    /**
     * Опції для селекту брендів
     */
    const brandOptions = useMemo(
        () => brands.map(brand => ({ value: brand.id, label: brand.name })),
        [brands]
    );

    /**
     * Опції для селекту моделей
     */
    const modelOptions = useMemo(
        () => models.map(model => ({ value: model.id, label: model.name })),
        [models]
    );

    /**
     * Опції для селекту категорій
     */
    const categoryOptions = useMemo(
        () => categories.map(category => ({ value: category.id, label: category.name })),
        [categories]
    );

    /**
     * Плейсхолдер для селекту моделей
     */
    const modelPlaceholder = useMemo(() => {
        if (!selectedBrand) return PLACEHOLDERS.MODEL_NO_BRAND;
        if (loadingModels) return PLACEHOLDERS.MODEL_LOADING;
        return PLACEHOLDERS.MODEL_DEFAULT;
    }, [selectedBrand, loadingModels]);

    /**
     * Чи показувати фільтр "Марка авто"
     * Ховаємо, якщо обрана категорія не прив'язана до авто
     */
    const showBrand = useMemo(() => {
        if (!selectedCategory) return false;
        const catObj = categories.find(c => c.id === Number(selectedCategory));
        return !catObj || !CATEGORIES_WITHOUT_BRAND_MODEL.has(catObj.slug);
    }, [selectedCategory, categories]);

    /**
     * Чи показувати фільтр "Модель авто"
     * Ховаємо для brand-only категорій (де всі товари під моделлю "Інші")
     */
    const showModel = useMemo(() => {
        if (!showBrand) return false;
        const catObj = categories.find(c => c.id === Number(selectedCategory));
        return !catObj || !CATEGORIES_BRAND_ONLY.has(catObj.slug);
    }, [showBrand, selectedCategory, categories]);

    // ============================================================================
    // RENDER
    // ============================================================================

    return (
        <Layout className="home-page-layout">
            <SEO
                title="Автозапчастини б/у з Європи в Україні"
                description="UnionAuto - інтернет-магазин автозапчастин б/у з Європи в Україні. Підберіть оригінальні запчастини для вашого автомобіля. Доставка по Україні."
                canonical="/"
                structuredData={[
                    {
                        "@context": "https://schema.org",
                        "@type": "WebSite",
                        "name": "UnionAuto",
                        "url": typeof window !== 'undefined' ? window.location.origin : 'https://union-auto.com.ua',
                        "potentialAction": {
                            "@type": "SearchAction",
                            "target": {
                                "@type": "EntryPoint",
                                "urlTemplate": `${typeof window !== 'undefined' ? window.location.origin : 'https://union-auto.com.ua'}/search?q={search_term_string}`
                            },
                            "query-input": "required name=search_term_string"
                        }
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "Organization",
                        "name": "UnionAuto",
                        "url": typeof window !== 'undefined' ? window.location.origin : 'https://union-auto.com.ua',
                        "logo": `${typeof window !== 'undefined' ? window.location.origin : 'https://union-auto.com.ua'}/logo512.png`,
                        "description": "Інтернет-магазин автозапчастин та аксесуарів",
                        "address": {
                            "@type": "PostalAddress",
                            "addressCountry": "UA"
                        },
                        "contactPoint": {
                            "@type": "ContactPoint",
                            "telephone": COMPANY_PHONES[0].number,
                            "contactType": "customer service",
                            "email": COMPANY_EMAIL,
                            "availableLanguage": "Ukrainian"
                        }
                    }
                ]}
            />
            <div 
                className="home-page"
                style={{
                    backgroundImage: `
                        linear-gradient(
                            135deg,
                            rgba(45, 108, 223, 0.15) 0%,
                            rgba(77, 136, 237, 0.1) 50%,
                            rgba(248, 249, 250, 0.25) 100%
                        ),
                        url(${headerImage})
                    `
                }}
            >
                <div className="search-container">
                    <h1 className="search-title">Підбір автозапчастин</h1>
                    <p className="search-subtitle">Оберіть параметри для пошуку (один або декілька)</p>
                    
                    {/* Відображення помилок */}
                    {displayError && (
                        <div className="error-message" role="alert">
                            {displayError}
                        </div>
                    )}
                    
                    <div className="search-form">
                        {/* Селект категорії запчастин — завжди першим */}
                        <div className="select-group">
                            <label htmlFor="category-select" className="select-label">
                                Категорія запчастин
                            </label>
                            <CustomSelect
                                id="category-select"
                                value={selectedCategory}
                                onChange={handleCategoryChange}
                                disabled={loadingCategories}
                                placeholder={loadingCategories ? PLACEHOLDERS.CATEGORY_LOADING : PLACEHOLDERS.CATEGORY_DEFAULT}
                                options={categoryOptions}
                                maxHeight="300px"
                            />
                        </div>

                        {/* Марка — для всіх категорій що прив'язані до авто */}
                        {showBrand && (
                            <div className="select-group">
                                <label htmlFor="brand-select" className="select-label">
                                    Марка авто
                                </label>
                                <CustomSelect
                                    id="brand-select"
                                    value={selectedBrand}
                                    onChange={handleBrandChange}
                                    disabled={loadingBrands}
                                    placeholder={loadingBrands ? PLACEHOLDERS.BRAND_LOADING : PLACEHOLDERS.BRAND_DEFAULT}
                                    options={brandOptions}
                                    maxHeight="300px"
                                />
                            </div>
                        )}

                        {/* Модель — тільки для звичайних категорій (не brand-only) */}
                        {showModel && (
                            <div className="select-group">
                                <label htmlFor="model-select" className="select-label">
                                    Модель авто
                                </label>
                                <CustomSelect
                                    id="model-select"
                                    value={selectedModel}
                                    onChange={handleModelChange}
                                    disabled={!selectedBrand || loadingModels}
                                    placeholder={modelPlaceholder}
                                    options={modelOptions}
                                    maxHeight="300px"
                                />
                            </div>
                        )}

                        {/* Extra-фільтри категорії (напр. виробник для компресорів) */}
                        {selectedCategory && (() => {
                            const catObj = categories.find(c => c.id === Number(selectedCategory));
                            const extraFields = catObj ? (CATEGORY_EXTRA_FILTERS[catObj.slug] || []) : [];
                            return extraFields.map(field => (
                                <div key={field.key} className="select-group">
                                    <label className="select-label">{field.label}</label>
                                    <CustomSelect
                                        value={extraValues[field.key] || ''}
                                        onChange={e => setExtraValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                                        options={(extraOptions[field.key] || []).map(v => ({ value: v, label: v }))}
                                        placeholder={field.placeholder || 'Усі'}
                                        maxHeight="300px"
                                    />
                                </div>
                            ));
                        })()}

                        {/* Кнопка пошуку */}
                        <button
                            className="home-search-button"
                            onClick={handleSearch}
                            disabled={!isSearchEnabled}
                            type="button"
                            aria-label="Шукати запчастини за обраними параметрами"
                        >
                            Знайти запчастини
                        </button>
                    </div>
                </div>
            </div>

            {/* ================================================================
                СТАТИЧНІ ПОСИЛАННЯ НА КАТЕГОРІЇ — критично важливо для SEO.
                Google слідує тільки за <a href>, не за кнопками/дропдаунами.
                Цей блок дозволяє краулеру виявити всі сторінки категорій.
                ================================================================ */}
            {categories.length > 0 && (
                <section className="home-categories" aria-label="Категорії запчастин">
                    <h2 className="home-categories__title">Категорії запчастин</h2>
                    <ul className="home-categories__list">
                        {categories.map(cat => (
                            <li key={cat.id}>
                                <Link
                                    to={`/zapchastyny/${cat.slug || cat.id}`}
                                    className="home-categories__link"
                                >
                                    {cat.name}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* СТАТИЧНІ ПОСИЛАННЯ НА МАРКИ */}
            {brands.length > 0 && (
                <section className="home-brands" aria-label="Марки автомобілів">
                    <h2 className="home-brands__title">Марки автомобілів</h2>
                    <ul className="home-brands__list">
                        {brands.map(brand => {
                            const slug = brand.slug || brand.name?.toLowerCase().replace(/\s+/g, '-');
                            return (
                                <li key={brand.id}>
                                    <Link
                                        to={`/zapchastyny?brand=${slug}`}
                                        className="home-brands__link"
                                    >
                                        {brand.name}
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            )}
        </Layout>
    );
};

export default HomePage;
